import { useParams } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useEffect, useRef } from 'react'

import Body from '@/components/body/Body'
import { Box } from '@/components/ui/box'
import { Container } from '@/components/ui/container'
import { HEADER_HEIGHT } from '@/constants'
import { currentEnv } from '@/env/vars'
import useColorModeValue from '@/hooks/useColorModeValue'
import Chat from '@/pages/components/chat'
import LeftSide from '@/pages/components/gigiChat/leftSide/LeftSide'
import {
    chatSettingsAtom,
    chatSettingsExpandedAtom,
    cleanData,
    createChat,
    getChatSettings,
    getConversationsList,
    setConversation,
    wizzardProcesingStrategyAtom,
} from '@/store/gigiChat/ChatAtom'
import { getPinnedChats } from '@/store/gigiChat/PinAtom'
import { getSystemAgentVisuals } from '@/store/gigiChat/Visuals'
import type { IConversationSettings, TProcessingStrategy } from '@/types/gigiChat/ConversationSettings'

const ChatContainer = () => {
    const [showSidebar] = useAtom(chatSettingsExpandedAtom)
    const backgroundColor = useColorModeValue('white', 'dark.900')

    const params = useParams({ from: '/chat/$chatId' })
    const chatIdParam: string = params.chatId

    const [wizzardProcesingStrategy] = useAtom(wizzardProcesingStrategyAtom)
    const wizzardProcessingStrategyRef = useRef<TProcessingStrategy | undefined>(wizzardProcesingStrategy)
    wizzardProcessingStrategyRef.current = wizzardProcesingStrategy

    const [chatSettings] = useAtom(chatSettingsAtom)
    const chatSettingsRef = useRef(chatSettings)
    chatSettingsRef.current = chatSettings

    const getSettings = async (conversationId: string) => {
        const settings = (await getChatSettings(conversationId)) as IConversationSettings
        return settings
    }

    useEffect(() => {
        cleanData(false)
    }, [])

    useEffect(() => {
        const controller = new AbortController()
        const { signal } = controller
        void (async () => {
            await getConversationsList({ isInitial: true, signal }).then(() => {
                void getPinnedChats()
            })
        })()

        if (currentEnv.features.isEnabled('useAgent')) {
            void getSystemAgentVisuals()
        }

        return () => controller.abort('Another GET Conversations call was initiated')
    }, [])

    useEffect(() => {
        const controller = new AbortController()

        void (async () => {
            if (currentEnv.features.isEnabled('useAgent')) {
                void getSystemAgentVisuals()
            }

            if (chatIdParam) {
                await getSettings(chatIdParam).then((res) => {
                    if (!controller.signal.aborted) {
                        // restore from local if set and valid
                        const validProcessingStrategies: TProcessingStrategy[] = ['QA', 'Writing']

                        const storedProcessingStrategy = chatSettingsRef.current?.processingStrategy ?? res.processingStrategy

                        res.processingStrategy = validProcessingStrategies.includes(storedProcessingStrategy as TProcessingStrategy)
                            ? storedProcessingStrategy
                            : res.processingStrategy
                        setConversation(chatIdParam, res, res.processingStrategy, controller.signal)
                    }
                })
            } else {
                await createChat(wizzardProcessingStrategyRef.current, undefined, controller.signal)
            }
        })()

        return () => {
            controller.abort('Chat id changed')
        }
    }, [chatIdParam])

    return (
        <>
            <Container
                maxW="100%"
                h={[
                    `calc(100% - ${HEADER_HEIGHT.mobile})`,
                    `calc(100% - ${HEADER_HEIGHT.mobile})`,
                    `calc(100% - ${HEADER_HEIGHT.desktop})`,
                ]}
                display="flex"
                flexDirection="column"
                flexGrow="1"
                padding="0"
            >
                <Body>
                    <Box
                        maxWidth="400px"
                        width={showSidebar ? '100%' : '30px'}
                        mt="16px"
                        mr="16px"
                        height="calc(100% - 16px)"
                        borderRadius="0 8px"
                        boxShadow="0px 0px 8px 1px rgba(84, 79, 64, 0.06)"
                        borderLeft="0"
                        backgroundColor={backgroundColor}
                        display={['none', 'none', 'none', 'block']}
                    >
                        <LeftSide />
                    </Box>
                    <Box
                        flex={2}
                        position="relative"
                        mt="16px"
                        borderRadius={showSidebar ? '8px 0px 0px' : '8px 8px 0px 0px'}
                        boxShadow="0px 0px 8px 1px rgba(84, 79, 64, 0.06)"
                        borderRight="0"
                        backgroundColor={backgroundColor}
                        mx={showSidebar ? '0' : 'auto'}
                        maxW={showSidebar ? ['100%', '100%', '100%', '100%'] : ['100%', '100%', '100%', '80%']}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: 0,
                                right: 0,
                                paddingLeft: '24px',
                                paddingRight: '24px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Chat />
                        </div>
                    </Box>
                </Body>
            </Container>
        </>
    )
}

export default ChatContainer
